/**
 * `RENDER_DEFAULTS` — the small set of render-budget constants the UI used to derive
 * from `useRenderParams()` (the D-004 capability-tier table).
 *
 * When the local ONNX/CUDA embedding pipeline was retired in favour of OpenAI's
 * hosted embeddings (2026-05-17), the frontend no longer had a hardware tier to
 * tune render budgets against — inference moved off the local machine. The
 * call-site shape (`defaultPageSize`, `pollIntervalMs`, `searchRerankDefault`,
 * `graphMaxNodes`, `enableHeavyTransitions`) was preserved as static constants
 * here so each consumer stays a one-line import; tuning still happens in one place.
 *
 * Values mirror the prior `mid` tier — a balanced default for a typical laptop
 * webview, neither penny-pinching nor lavish.
 */
export const RENDER_DEFAULTS = {
  /** Initial page size for virtualized lists (search results, browse, health tables). */
  defaultPageSize: 50,
  /**
   * Poll interval (ms) for the auto-refreshing surfaces — Sources / Corpus Health
   * tabs in Settings, the status-bar cost-telemetry chip. TanStack Query pauses
   * polling when the route is not visible, so the cost is per-active-surface.
   */
  pollIntervalMs: 30_000,
  /**
   * Default value of the Search surface's "cross-encoder rerank" toggle. Cross-
   * encoder rerank ran locally via `bge-reranker-base`; it was dropped alongside
   * the local-embedding pipeline (OpenAI has no rerank API). Hybrid BM25+cosine
   * is the new ceiling.
   */
  searchRerankDefault: false,
  /**
   * Hard cap on graph node count rendered by `<WebGLGraphCanvas>`. A laptop
   * browser can still struggle with a 5K-node force-directed layout regardless
   * of where embedding happens, so this stays a real (frontend-render-side) cap.
   */
  graphMaxNodes: 1500,
  /** Whether to use the costlier CSS transitions/animations. */
  enableHeavyTransitions: true,
} as const;
